import React from "react";
import styled from "styled-components";
import { Search, X } from "react-feather";
import { spacing } from "../../../design-system/tokens/spacing";
import { typographyMixin } from "../../../design-system/tokens/typography";
import { rawColors } from "../../../design-system/tokens/colors";
import { borderRadius } from "../../../design-system/tokens/borders";
import { Spinner, TreeNodeRow } from "../../atoms";

export interface SchemaTreeTable {
  schema: string;
  table: string;
}

export interface SchemaTreeNode {
  name: string;
  /** Tables to render under the schema. Tables that fail the active search
   *  filter should be excluded by the parent before passing in. */
  tables?: SchemaTreeTable[];
}

export interface SchemaTreeProps {
  schemas: SchemaTreeNode[];
  expandedSchemas: ReadonlySet<string>;
  selectedTables: ReadonlySet<string>;
  previewTarget: SchemaTreeTable | null;
  /** Schema names currently fetching their tables (for the inline loading row). */
  loadingSchemas: ReadonlySet<string>;
  /** Returns the qualified key the parent uses for `selectedTables`. */
  qualifiedKey: (schema: string, table: string) => string;

  searchQuery: string;
  searchOpen: boolean;
  onSearchToggle: (open: boolean) => void;
  onSearchChange: (q: string) => void;

  onToggleSchemaExpand: (name: string) => void;
  onToggleTableSelect: (
    schema: string,
    table: string,
    next: boolean,
  ) => void;
  onPreviewTable: (schema: string, table: string) => void;

  /**
   * Whether a given table row can be previewed (shows the eye icon).
   * Defaults to always-previewable when omitted, so every existing caller
   * keeps today's behavior unchanged. Used by Salesforce to hide the
   * preview affordance on report/dashboard rows while keeping it on Object
   * rows within the same tree.
   */
  isRowPreviewable?: (schema: string, table: string) => boolean;

  /** Optional UI copy overrides (e.g. HubSpot uses Objects instead of Schemas). */
  headerLabel?: string;
  searchPlaceholder?: string;
  emptyChildLabel?: string;
  loadingChildLabel?: string;

  className?: string;
}

const Card = styled.aside`
  display: flex;
  flex-direction: column;
  width: 220px;
  flex: 0 0 auto;
  align-self: stretch;
  min-height: 0;
  height: 520px;
  background-color: ${rawColors.structural.white};
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius.lg};
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[2]};
  padding: ${spacing[2]} ${spacing[3]};
  border-bottom: 1px solid ${rawColors.stroke[200]};
  flex: 0 0 auto;
`;

const HeaderTitle = styled.span`
  ${typographyMixin("body", 3)}
  color: ${rawColors.text.highEmphasis};
`;

const IconButton = styled.button`
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: transparent;
  border: none;
  color: ${rawColors.text.mediumEmphasis};
  cursor: pointer;
`;

const SearchInput = styled.input`
  flex: 1 1 auto;
  min-width: 0;
  padding: 0;
  border: none;
  background: transparent;
  outline: none;
  ${typographyMixin("body", 4)}
  color: ${rawColors.text.highEmphasis};

  &::placeholder {
    color: ${rawColors.text.lowEmphasis};
  }
`;

/* Tree list scrolls inside the card if it overflows — never the page. */
const ListScroll = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: ${spacing[1]} 0 ${spacing[2]};
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const SchemaGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

/* Children container draws the vertical guide line that runs from below the
 * parent schema's expand-button down through the children. The line sits at
 * x = 18px (centered on the 12px-wide expand button which itself starts at
 * the row's 12px left padding → button center is at 12 + 6 = 18px).
 *
 * Each table row inside also gets a horizontal stub via its own ::before
 * (see below) connecting from the line out to its checkbox. */
const SchemaChildren = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  &::before {
    content: "";
    position: absolute;
    left: 18px;
    top: 0;
    bottom: 6px;
    width: 1px;
    background-color: ${rawColors.stroke[300]};
  }

  /* Horizontal stub from the guide line to the start of the checkbox column,
   * drawn on each direct child row. The row's left padding is 32px and the
   * line sits at x=18px, so the stub is 32 - 18 = 14px long, with 1px width
   * minus the line's own pixel for a clean join. */
  > [role="treeitem"]::before {
    content: "";
    position: absolute;
    left: 18px;
    top: 50%;
    width: 13px;
    height: 1px;
    background-color: ${rawColors.stroke[300]};
  }
`;

const InlineRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  padding: ${spacing[1]} ${spacing[3]} ${spacing[1]} 32px;
  ${typographyMixin("caption", 3)}
  color: ${rawColors.text.mediumEmphasis};
`;

export const SchemaTree: React.FC<SchemaTreeProps> = ({
  schemas,
  expandedSchemas,
  selectedTables,
  previewTarget,
  loadingSchemas,
  qualifiedKey,
  searchQuery,
  searchOpen,
  onSearchToggle,
  onSearchChange,
  onToggleSchemaExpand,
  onToggleTableSelect,
  onPreviewTable,
  isRowPreviewable,
  headerLabel,
  searchPlaceholder,
  emptyChildLabel,
  loadingChildLabel,
  className,
}) => {
  const resolvedHeaderLabel = headerLabel ?? "Schemas";
  const resolvedSearchPlaceholder = searchPlaceholder ?? "Search schemas and tables";
  const resolvedEmptyChildLabel = emptyChildLabel ?? "No tables";
  const resolvedLoadingChildLabel = loadingChildLabel ?? "Loading tables…";
  return (
    <Card className={className}>
      <Header>
        {searchOpen ? (
          <>
            <SearchInput
              autoFocus
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={resolvedSearchPlaceholder}
              aria-label={resolvedSearchPlaceholder}
            />
            <IconButton
              type="button"
              aria-label="Close search"
              onClick={() => onSearchToggle(false)}
            >
              <X size={16} />
            </IconButton>
          </>
        ) : (
          <>
            <HeaderTitle>{resolvedHeaderLabel}</HeaderTitle>
            <IconButton
              type="button"
              aria-label={resolvedSearchPlaceholder}
              onClick={() => onSearchToggle(true)}
            >
              <Search size={16} />
            </IconButton>
          </>
        )}
      </Header>

      <ListScroll>
        {schemas.map((s) => {
          const expanded = expandedSchemas.has(s.name);
          const tables = s.tables ?? [];
          const isLoading = loadingSchemas.has(s.name);
          return (
            <SchemaGroup key={s.name}>
              <TreeNodeRow
                kind="schema"
                name={s.name}
                expanded={expanded}
                onToggleExpand={() => onToggleSchemaExpand(s.name)}
              />
              {expanded ? (
                <SchemaChildren>
                  {isLoading && tables.length === 0 ? (
                    <InlineRow>
                      <Spinner size={12} aria-hidden />
                      <span>{resolvedLoadingChildLabel}</span>
                    </InlineRow>
                  ) : tables.length === 0 ? (
                    <InlineRow>{resolvedEmptyChildLabel}</InlineRow>
                  ) : (
                    tables.map((t) => {
                      const key = qualifiedKey(t.schema, t.table);
                      const isPreview =
                        previewTarget !== null &&
                        previewTarget.schema === t.schema &&
                        previewTarget.table === t.table;
                      const previewable =
                        isRowPreviewable?.(t.schema, t.table) ?? true;
                      return (
                        <TreeNodeRow
                          key={key}
                          kind="table"
                          name={t.table}
                          selected={selectedTables.has(key)}
                          previewActive={isPreview}
                          previewable={previewable}
                          onToggleSelect={(next) =>
                            onToggleTableSelect(t.schema, t.table, next)
                          }
                          onPreviewClick={
                            previewable
                              ? () => onPreviewTable(t.schema, t.table)
                              : undefined
                          }
                        />
                      );
                    })
                  )}
                </SchemaChildren>
              ) : null}
            </SchemaGroup>
          );
        })}
      </ListScroll>
    </Card>
  );
};

export default SchemaTree;
